import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FolderOpen } from 'lucide-react';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow, Icon, ImageSlot, Pill, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { ReserveUnitDialog } from '../../reservations';
import { confirmBaseData, restoreProperty, useInvalidateProperty, usePropertyRecord } from '../api';
import {
  CardHead,
  Crumbs,
  DashedButton,
  DocumentRows,
  EyebrowInline,
  FieldPair,
  SourceBadge,
  TinyButton,
  documentRowItem,
} from '../bits';
import { activityDay, formatNumber } from '../format';
import { MediaGallery } from '../media/MediaGallery';
import { PICTURE_SIZES, coverItem, pictureOf } from '../media/gallery';
import { rentTotalRows } from '../rent';
import type { PropertyRecord, UnitRow } from '../types';
import { AcquisitionCalculationCard } from './AcquisitionCalculationCard';
import { ArchiveDialog } from './ArchiveDialog';
import { BaseDataSheet, BaseDataStatusLine } from './BaseDataOnboarding';
import { CalcDefaultsCard } from './CalcDefaultsCard';
import { ChecklistSummary } from './ChecklistSummary';
import { DescriptionCard } from './DescriptionCard';
import { folderHost } from './documentsFolder';
import { OwnerUploadDialog } from './OwnerUploadDialog';
import { PropertyEditDialog } from './PropertyEditDialog';
import { UnitsCard } from './UnitsCard';
import styles from './PropertyRecordPage.module.css';

type DialogKey = 'edit' | 'archive' | 'upload' | 'baseData' | null;

function RecordSkeleton() {
  return (
    <div className={styles.stack} aria-busy="true" aria-label="Wird geladen">
      <Skeleton variant="block" height={220} className={styles.skeletonHero} />
      <div className={styles.metrics}>
        {Array.from({ length: 5 }, (_, index) => (
          <Skeleton key={index} variant="block" height={84} />
        ))}
      </div>
      <div className={styles.grid}>
        <div className={styles.column}>
          <Skeleton variant="block" height={220} />
          <Skeleton variant="block" height={520} />
        </div>
        <div className={styles.column}>
          <Skeleton variant="block" height={420} />
          <Skeleton variant="block" height={150} />
        </div>
      </div>
    </div>
  );
}

/** Objekte 08: the read-only archived record with „Wiederherstellen" and the unit breakdown. */
function ArchivedRecord({
  record,
  onRestored,
}: {
  record: PropertyRecord;
  onRestored: (record: PropertyRecord) => void;
}) {
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const archived = record.archived;
  if (!archived) return null;

  async function restore() {
    setPending(true);
    try {
      onRestored(await restoreProperty(record.id));
      toast({ message: 'Das Objekt ist wiederhergestellt.' });
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }

  const reservedLine =
    archived.units.reserved.length === 0
      ? '0'
      : `${formatInteger(archived.units.reserved.length)} · ${archived.units.reserved.map((unit) => unit.number).join(', ')}`;
  const inDealLine =
    archived.units.inDeal.length === 0 ? '—' : archived.units.inDeal.map((unit) => unit.number).join(', ');

  return (
    <div className={styles.archivedPage}>
      <div className={styles.archivedHead}>
        <div className={styles.archivedText}>
          <Crumbs items={[{ label: 'Objekte', href: '/properties' }, { label: 'Archiv' }]} separator="/" />
          <div className={styles.archivedTitleRow}>
            <h1 className={styles.archivedTitle}>{record.name}</h1>
            <Pill variant="neutral">{record.statusLabel}</Pill>
          </div>
          <div className={styles.archivedMeta}>{archived.label}</div>
        </div>
        {record.canRestore ? (
          <div className={styles.archivedActions}>
            <Button loading={pending} onClick={() => void restore()}>
              Wiederherstellen
            </Button>
          </div>
        ) : null}
      </div>
      <div className={styles.readOnlyBox} role="status">
        {archived.notice}
      </div>
      <section className={styles.card} aria-labelledby="archived-units">
        <Eyebrow id="archived-units">Einheiten</Eyebrow>
        <div className={styles.breakdown}>
          <div className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>Verkauft</span>
            <span className={styles.breakdownValue}>{formatInteger(archived.units.sold)}</span>
          </div>
          <div className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>Reserviert</span>
            <span className={styles.breakdownValue}>{reservedLine}</span>
          </div>
          <div className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>Nicht verfügbar</span>
            <span className={styles.breakdownValue}>{formatInteger(archived.units.unavailable)}</span>
          </div>
          <div className={styles.breakdownRow}>
            <span className={styles.breakdownLabel}>Im Deal</span>
            <span className={styles.breakdownValue}>{inDealLine}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Objekte 03 (record) and 08 (archived) at `/properties/:id`. */
/** „davon 3 aus TeamProQ" under a metric when part of the count is reported by TeamProQ. */
function ExternalPart({ count }: { count: number }) {
  if (count <= 0) return null;
  return <div className={styles.metricNote}>davon {formatInteger(count)} aus TeamProQ</div>;
}

/**
 * The buckets beside the four status tiles, only when they occur: exclusively reserved in TeamProQ and
 * not released for sale („2 exklusiv reserviert · 3 nicht freigegeben").
 */
function MetricsExtra({ exclusive, notReleased }: { exclusive: number; notReleased: number }) {
  if (exclusive <= 0 && notReleased <= 0) return null;
  return (
    <div className={styles.metricsExtra}>
      {exclusive > 0 ? (
        <span className={styles.metricsExtraItem}>
          <strong>{formatInteger(exclusive)}</strong> exklusiv reserviert (TeamProQ)
        </span>
      ) : null}
      {notReleased > 0 ? (
        <span className={styles.metricsExtraItem}>
          <strong>{formatInteger(notReleased)}</strong> nicht freigegeben
        </span>
      ) : null}
    </div>
  );
}

export function PropertyRecordPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const invalidate = useInvalidateProperty();
  const record = usePropertyRecord(id);
  const [dialog, setDialog] = useState<DialogKey>(null);
  const [reserveUnit, setReserveUnit] = useState<UnitRow | null>(null);
  const [confirming, setConfirming] = useState(false);

  const crumbs = (label: string) => (
    <Crumbs items={[{ label: 'Objekte', href: '/properties' }, { label }]} className={styles.crumbs} />
  );

  if (record.isPending) {
    return (
      <div className={styles.page}>
        {crumbs('wird geladen')}
        <RecordSkeleton />
      </div>
    );
  }
  if (record.isError) {
    const notFound = record.error instanceof ApiError && record.error.status === 404;
    return (
      <div className={styles.page}>
        {crumbs(notFound ? 'nicht gefunden' : 'Fehler')}
        <Banner
          tone={notFound ? 'neutral' : 'danger'}
          title={
            notFound ? 'Das Objekt wurde nicht gefunden.' : 'Die Objektakte konnte nicht geladen werden.'
          }
          actions={
            <>
              <Button variant="secondary" size="small" onClick={() => void navigate('/properties')}>
                Zur Objektübersicht
              </Button>
              {notFound ? null : (
                <Button variant="ghost" size="small" onClick={() => void record.refetch()}>
                  Erneut versuchen
                </Button>
              )}
            </>
          }
        >
          {notFound
            ? 'Der Link zeigt auf kein Objekt in Ihrem Bereich — vielleicht wurde es zurückgezogen oder archiviert.'
            : errorMessage(record.error)}
        </Banner>
      </div>
    );
  }

  const data = record.data;
  const refresh = () => void invalidate(id);

  if (data.status === 'archived' && data.archived) {
    return (
      <ArchivedRecord
        record={data}
        onRestored={() => {
          refresh();
        }}
      />
    );
  }

  const documents = [
    ...data.documents.items.map(documentRowItem),
    ...data.documents.missing.map((missing) => ({
      key: `missing-${missing.checklistItemId}`,
      name: missing.title,
      meta: 'Fehlt',
      tone: 'missing' as const,
    })),
  ];
  const documentCount = data.documents.total;
  const cover = coverItem(data.media);
  const heroFileId = cover?.fileId ?? data.hero.heroFileId;
  const hero = heroFileId ? pictureOf(heroFileId, { size: cover ?? undefined }) : null;

  async function confirm() {
    setConfirming(true);
    try {
      await confirmBaseData(id);
      toast({ message: 'Stammdaten bestätigt.' });
      refresh();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div className={styles.page}>
      {crumbs(data.name)}
      <div className={styles.stack}>
        <div className={styles.hero}>
          <ImageSlot
            chrome="frame"
            src={hero?.src}
            srcSet={hero?.srcSet}
            sizes={PICTURE_SIZES.hero}
            fetchPriority="high"
            alt={cover?.caption ?? `Objektbild · ${data.name}`}
            placeholder={`Objektbild · ${data.name}`}
            height={220}
            radius="none"
            className={styles.heroImage}
          />
          <div className={styles.heroScrim} aria-hidden="true" />
          <div className={styles.heroOverlay}>
            <div className={styles.heroText}>
              <div className={styles.heroEyebrowRow}>
                <div className={styles.heroEyebrow}>{data.hero.eyebrow}</div>
                {data.external ? <SourceBadge external={data.external} /> : null}
              </div>
              <h1 className={styles.heroTitle}>{data.hero.title}</h1>
              <div className={styles.heroSubtitle}>{data.hero.subtitle}</div>
            </div>
            <div className={styles.heroActions}>
              {data.canEdit ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.glassButton)}
                  onClick={() => setDialog('edit')}
                >
                  Objekt bearbeiten
                </button>
              ) : null}
              {data.canArchive ? (
                <button
                  type="button"
                  className={clsx('pressable', styles.glassButton)}
                  onClick={() => setDialog('archive')}
                >
                  Archivieren
                </button>
              ) : null}
              {data.status === 'acquisition' ? (
                <Button
                  className={styles.heroPrimary}
                  onClick={() => void navigate(`/properties/${id}/wizard`)}
                >
                  Zum Ankauf
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.metrics}>
          <div className={styles.metric}>
            <Eyebrow>Einheiten</Eyebrow>
            <div className={styles.metricValue}>{formatInteger(data.metrics.units)}</div>
          </div>
          <div className={styles.metric}>
            <Eyebrow>Frei</Eyebrow>
            <div className={styles.metricValue}>{formatInteger(data.metrics.available)}</div>
          </div>
          <div className={styles.metric}>
            <Eyebrow>Reserviert</Eyebrow>
            <div className={clsx(styles.metricValue, styles.metricAccent)}>
              {formatInteger(data.metrics.reserved)}
            </div>
            <ExternalPart count={data.metrics.externalReserved} />
          </div>
          <div className={styles.metric}>
            <Eyebrow>Verkauft</Eyebrow>
            <div className={clsx(styles.metricValue, styles.metricSuccess)}>
              {formatInteger(data.metrics.sold)}
            </div>
            <ExternalPart count={data.metrics.externalSold} />
          </div>
          <div className={styles.metric}>
            <Eyebrow>Kaufpreise</Eyebrow>
            <div className={styles.metricRange}>{data.metrics.priceRange?.label ?? '—'}</div>
          </div>
        </div>
        <MetricsExtra exclusive={data.metrics.exclusive} notReleased={data.metrics.notReleased} />
        <BaseDataStatusLine
          record={data}
          onComplete={data.canEdit ? () => setDialog('baseData') : null}
          onConfirm={data.canEdit ? () => void confirm() : null}
          confirming={confirming}
        />

        <div className={styles.grid}>
          <div className={styles.column}>
            <section className={styles.card} aria-labelledby="property-data">
              <CardHead
                eyebrow="Objektdaten"
                id="property-data"
                end={
                  data.canEdit ? (
                    <TinyButton onClick={() => setDialog('edit')}>Bearbeiten</TinyButton>
                  ) : undefined
                }
              />
              <div className={styles.dataGrid}>
                <FieldPair label="Baujahr" value={data.data.yearBuilt} />
                <FieldPair
                  label="Sanierungsjahr"
                  value={data.fields.yearRenovated === null ? '—' : String(data.fields.yearRenovated)}
                />
                <FieldPair label="Bauabschnitte" value={data.data.constructionPhases} />
                <FieldPair label="Wohnfläche gesamt" value={data.data.livingArea} />
                <FieldPair label="Energieausweis" value={data.data.energyCertificate} />
                <FieldPair label="Heizung" value={data.data.heating} />
                <FieldPair label="Stellplätze" value={data.data.parking} />
                <FieldPair label="Bezugsfertig" value={data.data.readyForOccupancy} />
                <FieldPair label="Provision" value={data.data.commission} />
                <FieldPair label="Bundesland" value={data.data.state} />
                <FieldPair label="Grunderwerbsteuer" value={data.data.landTransferTax} />
                <FieldPair label="Notar" value={data.data.notary} />
                <FieldPair label="Teilungserklärung" value={data.data.declarationOfDivision} />
                <FieldPair label="Hausgeld Ø" value={data.data.serviceCharge} />
                <FieldPair label="Objektbetreuung" value={data.data.manager} />
                <FieldPair
                  label="MEA gesamt"
                  value={
                    data.fields.coOwnershipSharesTotal === null
                      ? '—'
                      : formatNumber(data.fields.coOwnershipSharesTotal, 4)
                  }
                />
                {rentTotalRows(data.rentTotals).map((row) => (
                  <FieldPair key={row.label} label={row.label} value={row.value} />
                ))}
              </div>
            </section>

            <DescriptionCard fields={data.fields} />

            <UnitsCard
              propertyId={id}
              propertyName={data.name}
              canEdit={data.canEdit}
              onReserve={setReserveUnit}
            />

            <MediaGallery
              owner={{ propertyId: id }}
              items={data.media}
              canEdit={data.canEdit}
              external={data.external}
              headingId="property-media"
              onChanged={refresh}
            />
          </div>

          <div className={styles.column}>
            <CalcDefaultsCard
              record={data}
              onSaved={() => {
                toast({ message: 'Objekt-Vorgaben gespeichert.' });
                refresh();
              }}
            />
            {data.status === 'acquisition' ? <AcquisitionCalculationCard propertyId={id} /> : null}
            <section className={styles.card} aria-labelledby="property-documents">
              <CardHead
                eyebrow="Objektunterlagen"
                id="property-documents"
                end={`${formatInteger(documentCount)} ${documentCount === 1 ? 'Dokument' : 'Dokumente'}`}
              />
              {data.status === 'acquisition' ? <ChecklistSummary propertyId={id} /> : null}
              {data.fields.documentsUrl ? (
                <div className={styles.folderRow}>
                  <span className={styles.folderIcon}>
                    <Icon icon={FolderOpen} size={16} />
                  </span>
                  <span className={styles.folderText}>
                    <span className={styles.folderLabel}>Externer Unterlagenordner</span>
                    <span className={styles.folderHost} title={data.fields.documentsUrl}>
                      {folderHost(data.fields.documentsUrl)}
                    </span>
                  </span>
                  <a
                    className={clsx('pressable', styles.folderLink)}
                    href={data.fields.documentsUrl}
                    target="_blank"
                    rel="noopener"
                  >
                    Ordner öffnen
                  </a>
                </div>
              ) : null}
              <DocumentRows
                items={documents}
                emptyText="Noch keine Unterlagen. Laden Sie Exposé, Teilungserklärung und Baubeschreibung hoch."
              />
              {data.canEdit ? (
                <DashedButton onClick={() => setDialog('upload')}>Unterlagen hochladen</DashedButton>
              ) : null}
            </section>

            <section className={styles.card} aria-labelledby="property-contacts">
              <EyebrowInline id="property-contacts">Ansprechpartner</EyebrowInline>
              <div className={styles.contacts}>
                {data.contacts.map((contact) => (
                  <div key={`${contact.kind}-${contact.name}`} className={styles.contact}>
                    <span
                      role="img"
                      aria-label={contact.name}
                      className={clsx(
                        styles.contactAvatar,
                        contact.kind === 'developer' && styles.contactAvatarSand,
                      )}
                    >
                      {contact.initials}
                    </span>
                    <div className={styles.contactText}>
                      <div className={styles.contactName}>{contact.name}</div>
                      <div className={styles.contactRole}>{contact.role}</div>
                    </div>
                  </div>
                ))}
                {data.contacts.length === 0 ? (
                  <p className={styles.emptyText}>Noch keine Ansprechpartner hinterlegt.</p>
                ) : null}
              </div>
            </section>

            <section className={styles.darkCard} aria-labelledby="property-activity">
              <EyebrowInline id="property-activity" className={styles.darkEyebrow}>
                Aktivität
              </EyebrowInline>
              <div className={styles.activity}>
                {data.activity.length === 0 ? (
                  <p className={styles.activityEmpty}>Noch keine Aktivität.</p>
                ) : (
                  data.activity.map((entry) => (
                    <div key={entry.id}>
                      <div className={styles.activityTitle}>
                        {entry.title}
                        {entry.detail ? ` · ${entry.detail}` : ''}
                      </div>
                      <div className={styles.activityMeta}>
                        {activityDay(entry.at)} · {entry.actor}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      <PropertyEditDialog
        open={dialog === 'edit'}
        record={data}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          toast({ message: 'Objekt gespeichert.' });
          refresh();
        }}
      />
      <BaseDataSheet
        open={dialog === 'baseData'}
        record={data}
        onClose={() => setDialog(null)}
        onSaved={(_saved, confirmed) => {
          setDialog(null);
          toast({ message: confirmed ? 'Stammdaten gespeichert und bestätigt.' : 'Stammdaten gespeichert.' });
          refresh();
        }}
      />
      <ArchiveDialog
        open={dialog === 'archive'}
        record={data}
        onClose={() => setDialog(null)}
        onArchived={() => {
          setDialog(null);
          toast({ message: `${data.name} ist archiviert.` });
          refresh();
        }}
      />
      <OwnerUploadDialog
        open={dialog === 'upload'}
        owner={{ propertyId: id }}
        eyebrow="Objektunterlagen"
        title="Unterlagen hochladen"
        onClose={() => setDialog(null)}
        onUploaded={(row) => {
          setDialog(null);
          toast({ message: `Hochgeladen: ${row.name}. Die Datei wird geprüft.` });
          refresh();
        }}
      />
      {reserveUnit ? (
        <ReserveUnitDialog
          open
          unitId={reserveUnit.id}
          onClose={() => setReserveUnit(null)}
          onReserved={() => {
            setReserveUnit(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
